/** Pagina di un torneo: setup (coppie e gironi) → in corso (risultati) → concluso. */
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpDown, ChevronLeft, CloudUpload, Flag, Play, Printer, RefreshCw, Shuffle, Trash2, TriangleAlert, Undo2, UserPlus, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Tabs } from "@/components/ui/Tabs";
import {
  errMsg, fmtWhen, FORMAT_LABEL, gare, isNetworkError, pending, type GaraConfig, type GaraFormat, type GaraState, type LineupTeam, type Match, type Player,
} from "@/lib/gare";
import { GiroLineup } from "./GiroLineup";
import { Lineup } from "./Lineup";
import { FinalTable, GroupTable, Knockout, MatchList } from "./Matches";
import { PendingCtx, useOnline, usePendingFor, usePendingSync } from "./offline";
import { garaPath, Modal, Section, StatusBadge, useActor, useBase, useFlash } from "./ui";

type Tab = "partite" | "gironi" | "finale" | "classifica";

export default function GaraPage() {
  const gid = Number(useParams().id);
  const base = useBase();
  const nav = useNavigate();
  const qc = useQueryClient();
  const actor = useActor();
  const [flash, show] = useFlash();
  const [tabSel, setTab] = useState<Tab | null>(null);
  const key = ["gare", "gara", gid];
  const q = useQuery({
    queryKey: key,
    queryFn: () => gare.get(gid),
    refetchInterval: (query) => (query.state.data?.gara.status === "live" ? 10_000 : false),
  });
  const online = useOnline();
  const waiting = usePendingFor(gid);
  usePendingSync((pg, st) => {
    qc.setQueryData(["gare", "gara", pg], st);
    qc.invalidateQueries({ queryKey: ["gare", "list"] });
  }, show);

  const run = async (fn: () => Promise<GaraState>, ok?: string) => {
    try {
      const s = await fn();
      qc.setQueryData(key, s);
      qc.invalidateQueries({ queryKey: ["gare", "list"] });
      if (ok) show("success", ok);
      return s;
    } catch (e) {
      show("error", errMsg(e));
      q.refetch();
      throw e;
    }
  };
  const runQuiet = (fn: () => Promise<GaraState>, ok?: string) => run(fn, ok).catch(() => undefined);

  if (q.isLoading) return <p className="p-6 text-sm text-slate-500">Caricamento…</p>;
  if (q.isError || !q.data) {
    return <p className="p-6 text-sm text-red-600">Errore: {errMsg(q.error)} <button className="underline" onClick={() => q.refetch()}>Riprova</button></p>;
  }
  const s = q.data;
  const g = s.gara;
  const tab: Tab = tabSel ?? (g.status === "finished" ? "classifica" : "partite");
  const saveResult = async (m: Match, a: number | null, b: number | null) => {
    try {
      const st = await gare.result(gid, m.id, a, b, m.version);
      qc.setQueryData(key, st);
      qc.invalidateQueries({ queryKey: ["gare", "list"] });
      pending.remove(gid, m.id);
      show("success", a === null ? "Risultato cancellato" : "Risultato salvato");
    } catch (e) {
      if (isNetworkError(e)) { // niente rete: resta sul telefono e parte da solo
        pending.add({ gid, mid: m.id, games_a: a, games_b: b, version: m.version, ts: Date.now() });
        show("info", "Niente connessione: il risultato resta sul telefono e parte da solo appena torna la rete");
        return;
      }
      show("error", errMsg(e));
      q.refetch();
      throw e;
    }
  };
  const waitingMap = Object.fromEntries(waiting.map((p) => [p.mid, p]));

  return (
    <PendingCtx.Provider value={waitingMap}>
    <div className="space-y-4 pb-24">
      {flash}
      {!online || waiting.length ? (
        <div className={`sticky top-16 z-30 flex items-start gap-2 rounded-xl border p-3 text-sm ${online ? "border-sky-200 bg-sky-50 text-sky-800" : "border-amber-300 bg-amber-50 text-amber-900"}`}>
          {online ? <CloudUpload className="mt-0.5 h-4 w-4 shrink-0" /> : <WifiOff className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>
            {online ? "" : "Sei senza connessione. "}
            {waiting.length
              ? `${waiting.length} risultat${waiting.length === 1 ? "o" : "i"} in attesa di invio: ${waiting.length === 1 ? "è salvato" : "sono salvati"} sul telefono e ${waiting.length === 1 ? "parte" : "partono"} da solo appena torna la rete. Se chiudi la pagina, riaprila quando torna la connessione.`
              : "Puoi continuare a inserire i risultati: restano sul telefono e partono da soli appena torna la rete."}
          </span>
        </div>
      ) : null}
      <div>
        <Link to={base} className="mb-2 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
          <ChevronLeft className="h-4 w-4" /> Tutti i tornei
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-slate-900">{g.name}</h1>
            <p className="text-sm text-slate-500">
              {[fmtWhen(g.start_at), g.category, g.format_label].filter(Boolean).join(" · ")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <StatusBadge status={g.status} />
            {g.road_to ? <Badge variant="court">Road To</Badge> : <Badge>fuori Road To</Badge>}
            {g.status !== "setup" ? <Badge variant="info">{s.progress.done}/{s.progress.total} partite</Badge> : null}
          </div>
        </div>
      </div>

      {s.warnings.length ? (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{s.warnings.join(" · ")}</span>
        </div>
      ) : null}

      {g.status === "setup" ? (
        <Setup state={s} run={run} runQuiet={runQuiet} isAdmin={!!actor.data?.admin}
          onDeleted={() => nav(base)} show={show} />
      ) : (
        <>
          <Tabs<Tab> value={tab} onChange={setTab} items={[
            { value: "partite", label: "Partite", count: s.matches.filter((m) => m.status !== "done").length },
            ...(g.format === "gironi_tabellone" ? [
              { value: "gironi" as const, label: "Gironi" },
              { value: "finale" as const, label: "Fase finale" },
            ] : []),
            { value: "classifica", label: "Classifica" },
          ]} />
          {tab === "partite" ? <MatchList state={s} onSave={saveResult} readOnly={g.status === "finished"} /> : null}
          {tab === "gironi" ? (
            <div className="grid gap-3 lg:grid-cols-2">
              {Object.entries(s.groups).map(([lab, rows]) => (
                <GroupTable key={lab} title={`Girone ${lab}`} rows={rows} qualifiers={g.config.per_group ?? 2} />
              ))}
            </div>
          ) : null}
          {tab === "finale" ? (
            <Knockout state={s} onSave={saveResult} readOnly={g.status === "finished"}
              onTeams={(m, b) => runQuiet(() => gare.matchTeams(gid, m.id, b), "Squadre aggiornate")} />
          ) : null}
          {tab === "classifica" ? (
            <div className="space-y-4">
              {g.format !== "gironi_tabellone" && !(g.status === "finished" && s.final.length) ? (
                <GroupTable title={g.format === "giro_a_8" ? "Classifica individuale (live)" : "Classifica (live)"}
                  rows={g.format === "giro_a_8" ? s.individual : (Object.values(s.groups)[0] || [])}
                  qualifiers={0} nameHeader={g.format === "giro_a_8" ? "Giocatore" : "Coppia"} />
              ) : null}
              <FinalTable rows={s.final} provisional={g.status === "live"} roadTo={!!g.road_to}
                emptyText={g.format === "gironi_tabellone" ? undefined : "La classifica finale con i punti compare quando tutte le partite hanno un risultato."} />
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4">
            {g.status === "live" ? (
              <>
                <Button size="lg" disabled={!s.final.length}
                  onClick={() => window.confirm("Chiudere il torneo? La classifica diventa definitiva e i punti Road To vengono assegnati.")
                    && runQuiet(() => gare.close(gid), "Torneo chiuso")}>
                  <Flag className="h-4 w-4" /> Chiudi torneo
                </Button>
                <Button variant="outline" onClick={async () => {
                  if (!window.confirm("Tornare al setup? Le partite generate vengono cancellate.")) return;
                  try {
                    await run(() => gare.reset(gid));
                  } catch (e) {
                    const done = (e as { detail?: { done?: number } }).detail?.done;
                    if (done && window.confirm(`Ci sono già ${done} risultati. Cancellarli tutti e tornare al setup?`)) {
                      runQuiet(() => gare.reset(gid, true), "Tornato al setup");
                    }
                  }
                }}>
                  <Undo2 className="h-4 w-4" /> Torna al setup
                </Button>
              </>
            ) : null}
            <Link to={`${garaPath(base, gid)}/stampa`}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Printer className="h-4 w-4" /> Foglio di riserva
            </Link>
            {g.status === "finished" && actor.data?.admin ? (
              <Button variant="outline" onClick={() => window.confirm("Riaprire il torneo? I punti Road To vengono tolti finché non lo richiudi.")
                && runQuiet(() => gare.reopen(gid), "Torneo riaperto")}>
                <Undo2 className="h-4 w-4" /> Riapri
              </Button>
            ) : null}
          </div>
        </>
      )}
    </div>
    </PendingCtx.Provider>
  );
}

function Setup({ state, run, runQuiet, isAdmin, onDeleted, show }: {
  state: GaraState;
  run: (fn: () => Promise<GaraState>, ok?: string) => Promise<GaraState>;
  runQuiet: (fn: () => Promise<GaraState>, ok?: string) => Promise<GaraState | undefined>;
  isAdmin: boolean;
  onDeleted: () => void;
  show: (kind: "success" | "error" | "info", msg: string) => void;
}) {
  const g = state.gara;
  const gid = g.id;
  const [menu, setMenu] = useState<Player | null>(null);
  const [newName, setNewName] = useState("");
  const [newLevel, setNewLevel] = useState("");
  const realCount = state.players.filter((p) => p.role === "player").length;

  const saveLineup = (teams: LineupTeam[]) => runQuiet(() => gare.lineup(gid, teams));
  const giro = g.format === "giro_a_8";

  return (
    <div className="space-y-4">
      <Settings key={g.format} state={state} runQuiet={runQuiet} />

      <Section title={giro ? `Giocatori · ${realCount}` : g.format === "coppie_fisse" ? `Coppie · ${realCount} giocatori` : `Coppie e gironi · ${realCount} giocatori`} right={
        <div className="flex flex-wrap gap-1.5">
          {g.playtomic_tid ? (
            <Button size="sm" variant="outline" onClick={async () => {
              try {
                const r = await gare.syncPlaytomic(gid);
                await run(async () => r, `Playtomic: +${r.sync.added} nuovi, −${r.sync.removed} cancellati`);
              } catch (e) {
                show("error", errMsg(e));
              }
            }}>
              <RefreshCw className="h-3.5 w-3.5" /> Aggiorna iscritti
            </Button>
          ) : null}
          {giro ? (
            <Button size="sm" variant="outline" onClick={() => runQuiet(() => gare.autoPair(gid), "Numeri assegnati per livello")}>
              <ArrowUpDown className="h-3.5 w-3.5" /> Ordina per livello
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => window.confirm("Rifare l'abbinamento automatico? Le coppie fatte a mano vengono rifatte (quelle iscritte insieme su Playtomic restano).")
              && runQuiet(() => gare.autoPair(gid), "Coppie rifatte")}>
              <Shuffle className="h-3.5 w-3.5" /> Abbinamento automatico
            </Button>
          )}
        </div>
      }>
        {giro ? (
          <GiroLineup state={state} onPlayerMenu={setMenu}
            onOrder={(ids) => runQuiet(() => gare.order(gid, ids))}
            onRestore={(p) => runQuiet(() => gare.patchPlayer(gid, p.id, { role: "player" }), `${p.name} rimesso in gioco`)} />
        ) : (
          <Lineup state={state} onSave={saveLineup} onPlayerMenu={setMenu} single={g.format === "coppie_fisse"}
            onRestore={(p) => runQuiet(() => gare.patchPlayer(gid, p.id, { role: "player" }), `${p.name} rimesso in gioco`)} />
        )}

        <form className="mt-4 flex flex-wrap items-end gap-2 border-t border-slate-100 pt-4" onSubmit={(e) => {
          e.preventDefault();
          if (!newName.trim()) return;
          const level = newLevel ? Number(newLevel.replace(",", ".")) : null;
          runQuiet(() => gare.addPlayer(gid, { name: newName, level: Number.isFinite(level) ? level : null }), `${newName} aggiunto`)
            .then(() => { setNewName(""); setNewLevel(""); });
        }}>
          <div className="min-w-[10rem] flex-1">
            <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="np-name">Aggiungi un giocatore non iscritto</label>
            <Input id="np-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nome e cognome" />
          </div>
          <div className="w-24">
            <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="np-lvl">Livello</label>
            <Input id="np-lvl" inputMode="decimal" value={newLevel} onChange={(e) => setNewLevel(e.target.value)} placeholder="es. 2,5" />
          </div>
          <Button type="submit" variant="outline"><UserPlus className="h-4 w-4" /> Aggiungi</Button>
        </form>
      </Section>

      <div className="sticky bottom-3 z-30 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur">
        <Button size="lg" className="flex-1" onClick={() => window.confirm("Avviare il torneo? Vengono generate le partite dei gironi.")
          && runQuiet(() => gare.start(gid), "Torneo avviato: buon divertimento!")}>
          <Play className="h-4 w-4" /> Avvia torneo
        </Button>
        {isAdmin ? (
          <Button size="lg" variant="ghost" aria-label="Elimina torneo" onClick={async () => {
            if (!window.confirm(`Eliminare "${g.name}" dalla Gestione Tornei? (Su Playtomic non cambia nulla.)`)) return;
            try {
              await gare.remove(gid);
              onDeleted();
            } catch (e) {
              show("error", errMsg(e));
            }
          }}>
            <Trash2 className="h-4 w-4 text-red-600" />
          </Button>
        ) : null}
      </div>

      {menu ? <PlayerMenu p={menu} onClose={() => setMenu(null)} gid={gid} runQuiet={runQuiet} giro={giro} /> : null}
    </div>
  );
}

function PlayerMenu({ p, gid, onClose, runQuiet, giro }: {
  p: Player; gid: number; onClose: () => void; giro: boolean;
  runQuiet: (fn: () => Promise<GaraState>, ok?: string) => Promise<GaraState | undefined>;
}) {
  const [name, setName] = useState(p.name);
  const [level, setLevel] = useState(p.level !== null ? String(p.level).replace(".", ",") : "");
  const act = (fn: () => Promise<GaraState>, ok: string) => runQuiet(fn, ok).then((r) => r && onClose());
  return (
    <Modal title={p.name} onClose={onClose}>
      <form className="space-y-3" onSubmit={(e) => {
        e.preventDefault();
        const lv = level ? Number(level.replace(",", ".")) : NaN;
        act(() => gare.patchPlayer(gid, p.id, { name, ...(Number.isFinite(lv) ? { level: lv } : {}) }), "Giocatore aggiornato");
      }}>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="pm-name">Nome</label>
          <Input id="pm-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="pm-lvl">Livello</label>
          <Input id="pm-lvl" inputMode="decimal" value={level} onChange={(e) => setLevel(e.target.value)} />
        </div>
        <Button type="submit" className="w-full">Salva</Button>
      </form>
      <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
        {giro && p.role === "player" ? (
          <Button variant="outline" className="w-full"
            onClick={() => act(() => gare.patchPlayer(gid, p.id, { role: "instructor" }), `${p.name} in campo come istruttore`)}>
            È un istruttore (gioca, niente classifica)
          </Button>
        ) : null}
        {p.role === "instructor" ? (
          <Button variant="outline" className="w-full"
            onClick={() => act(() => gare.patchPlayer(gid, p.id, { role: "player" }), `${p.name} torna giocatore`)}>
            Non è un istruttore: torna giocatore
          </Button>
        ) : null}
        <Button variant="outline" className="w-full"
          onClick={() => act(() => gare.patchPlayer(gid, p.id, { role: "fake" }), `${p.name} segnato come finto`)}>
          Segna come finto / non gioca
        </Button>
        {p.source === "manual" ? (
          <Button variant="ghost" className="w-full text-red-600"
            onClick={() => window.confirm(`Eliminare ${p.name}?`) && act(() => gare.deletePlayer(gid, p.id), `${p.name} eliminato`)}>
            Elimina
          </Button>
        ) : null}
      </div>
    </Modal>
  );
}

function Settings({ state, runQuiet }: {
  state: GaraState;
  runQuiet: (fn: () => Promise<GaraState>, ok?: string) => Promise<GaraState | undefined>;
}) {
  const g = state.gara;
  const c = g.config;
  const [courts, setCourts] = useState(c.courts.join(", "));
  const [minutes, setMinutes] = useState(String(c.group_minutes ?? ""));
  const [name, setName] = useState(g.name);
  const [category, setCategory] = useState(g.category);
  const patchCfg = (cfg: Partial<GaraConfig>) => runQuiet(() => gare.patch(g.id, { config: cfg }), "Impostazioni salvate");
  const sel = "h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm";
  return (
    <details className="rounded-xl border border-slate-200 bg-white shadow-sm" open={state.teams.length === 0}>
      <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-slate-900">
        Impostazioni · {FORMAT_LABEL[g.format] ?? g.format}
        {g.format === "gironi_tabellone" ? ` · ${c.groups === 1 ? "1 girone" : `${c.groups} gironi`}, passano ${c.per_group} per girone` : ""}
        {` · campi ${c.courts.join(", ")}`}
      </summary>
      <div className="grid gap-3 border-t border-slate-100 p-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="st-fmt">Formato</label>
          <select id="st-fmt" className={sel} value={g.format}
            onChange={(e) => window.confirm("Cambiare formato? Le impostazioni tornano quelle standard del nuovo formato.")
              && runQuiet(() => gare.patch(g.id, { format: e.target.value as GaraFormat }), "Formato cambiato")}>
            {(Object.keys(FORMAT_LABEL) as GaraFormat[]).map((f) => <option key={f} value={f}>{FORMAT_LABEL[f]}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="st-name">Nome</label>
            <Input id="st-name" value={name} onChange={(e) => setName(e.target.value)}
              onBlur={() => name !== g.name && runQuiet(() => gare.patch(g.id, { name }), "Nome aggiornato")} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="st-cat">Categoria</label>
            <Input id="st-cat" value={category} onChange={(e) => setCategory(e.target.value)}
              onBlur={() => category !== g.category && runQuiet(() => gare.patch(g.id, { category }), "Categoria aggiornata")} />
          </div>
        </div>
        {g.format === "gironi_tabellone" ? (<>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="st-groups">Gironi</label>
          <select id="st-groups" className={sel} value={c.groups} onChange={(e) => patchCfg({ groups: Number(e.target.value) })}>
            <option value={1}>1 girone</option>
            <option value={2}>2 gironi</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="st-q">Passano alla fase finale</label>
          <select id="st-q" className={sel} value={c.per_group} onChange={(e) => patchCfg({ per_group: Number(e.target.value) })}>
            {(c.groups === 1 ? [2, 4] : [1, 2]).map((n) => <option key={n} value={n}>{n} per girone</option>)}
          </select>
        </div>
        </>) : null}
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="st-courts">Campi (separati da virgola)</label>
          <Input id="st-courts" value={courts} onChange={(e) => setCourts(e.target.value)}
            onBlur={() => patchCfg({ courts: courts.split(",").map((x) => x.trim()).filter(Boolean) })} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="st-min">{g.format === "gironi_tabellone" ? "Minuti per partita di girone" : "Minuti per partita"}</label>
          <Input id="st-min" inputMode="numeric" value={minutes} onChange={(e) => setMinutes(e.target.value)}
            onBlur={() => minutes && patchCfg({ group_minutes: Number(minutes) })} />
        </div>
        {g.format === "gironi_tabellone" ? (<>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" className="h-4 w-4 accent-moving-500" checked={!!c.third_place}
              onChange={(e) => patchCfg({ third_place: e.target.checked })} />
            Si gioca la finale Diamond 3°-4° posto
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" className="h-4 w-4 accent-moving-500" checked={c.gold !== false}
              onChange={(e) => patchCfg({ gold: e.target.checked })} />
            Si gioca la fase Gold (5°-8° posto, set ai 6 a tempo)
          </label>
        </>) : null}
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" className="h-4 w-4 accent-moving-500" checked={!!g.road_to}
            onChange={(e) => runQuiet(() => gare.patch(g.id, { road_to: e.target.checked }), "Road To aggiornato")} />
          Assegna punti Road To
        </label>
      </div>
    </details>
  );
}
